import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { defer, EMPTY, Observable } from 'rxjs';
import { catchError, delay, map, retryWhen, take, tap } from 'rxjs/operators';
import { CustomApiModels } from '@mona/api';
import { TokenStorageService } from '@mona/auth';
import { ConfigService } from '@mona/config';
import { DataUpdateMessage, DataUpdateMessageModel, Encounter } from '@mona/models';
import { WsExtendableService } from '@mona/shared/utils';
import { transformApiDataUpdateMessage } from './transforms/data-update.transforms';

/**
 * Data updates ws service
 */
@Injectable({ providedIn: 'root' })
export class DataUpdatesWsService extends WsExtendableService<
    CustomApiModels.DataUpdateMessage,
    DataUpdateMessage<DataUpdateMessageModel>
> {
    /**
     * Constructor
     * @param configService ConfigService
     * @param tokenStorage
     */
    constructor(
        private configService: ConfigService,
        private tokenStorage: TokenStorageService,
    ) {
        super();
    }

    /**
     * Connects to encounter live-data WS (`/ws/pdms/{encounterId}/`).
     * Always opens a fresh socket (same pattern as PdmsUpdatesWsService / general).
     * @param encounterId EntityId<Encounter>
     */
    connect(encounterId: EntityId<Encounter>): Observable<DataUpdateMessage<DataUpdateMessageModel>> {
        return defer(() => {
            this.disconnect();
            const accessToken = this.tokenStorage.getAccessToken();
            const url = `${this.configService.get('api.coreWsUrl')}/ws/pdms/${encounterId}/`;
            this.setConfig(
                url,
                accessToken,
                undefined,
                environment.reconnectInterval,
                // 0 = no base reconnect(). Dual retry (base + outer retryWhen) can leave two
                // native sockets open after reestablish. Outer retryWhen + connectionReestablished
                // own recovery.
                0,
            );
            this.logger.log(`[ws] DataUpdatesWsService connecting to`, this.originalUrl);
            return super.connect();
        }).pipe(
            map((message: CustomApiModels.DataUpdateMessage) => transformApiDataUpdateMessage(message)),
            retryWhen(errors =>
                errors.pipe(
                    tap(error => console.warn('[ws] DataUpdates connect failed, retrying...', error)),
                    delay(environment.reconnectInterval),
                    take(environment.reconnectAttempts),
                ),
            ),
            catchError(e => {
                this.logger.error('DataUpdates WebSocket connection error', e);
                return EMPTY;
            }),
        );
    }
}
